import { Color, Spacing } from '@persona-id/design-tokens';
import {
  CircleInfoIcon,
  CircleMinusIcon,
  CirclePlusIcon,
  LinkExternalIcon,
  MinusIcon,
  PlusIcon,
} from '@persona-id/design-tokens/icons';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import GraphCanvasV2 from 'dashboard/components/GraphCanvasV2';
import GraphExportModal from 'dashboard/components/GraphExportModal';
import GraphExportModalV2 from 'dashboard/components/GraphExportModalV2';
import GraphHoverCard from 'dashboard/components/GraphHoverCard';
import { useEventBus } from 'dashboard/components/GraphModule/hooks/useEventBus';
import { useG6Wrapper } from 'dashboard/components/GraphModule/hooks/useG6Wrapper';
import { useGraphConfiguration } from 'dashboard/components/GraphModule/hooks/useGraphConfiguration';
import { useGraphExplorer } from 'dashboard/components/GraphModule/hooks/useGraphExplorer';
// TODO: Consolidate to use design-system tooltip
import TooltipComponent from 'dashboard/components/Tooltip';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { AccountType } from 'dashboard/models/account-type';
import { G6GraphUpdateMessage, Node, NodeId, nodeIdToHashable } from 'dashboard/models/graph';
import { ServerNodeFunctionsConfig } from 'dashboard/models/graph-backend';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { HStack } from 'design-system/components/Stack';
import { Heading, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { BASE } from 'design-system/variables/spacing';
import IconDownload from 'images/icons/upload.svg';
import { ACCOUNT_DATUM_TYPE } from 'lib/constants/graph';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';

const BORDER_WIDTH = 2;

const GraphContainer = styled.div`
  border: ${BORDER_WIDTH}px solid #e6e6ea;
  background: ${Color.CoreSlate50};
  border-radius: ${Spacing[2]};

  position: relative;
  display: flex;
`;

const CanvasContainer = styled.div<{ height: number; width: number }>`
  ${({ height, width }) => css`
    height: ${height}px;
    width: ${width}px;
  `}
`;

const DisabledOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;

  background: rgba(255, 255, 255, 0.5);
  border-radius: ${Spacing[2]};
  z-index: 5;

  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const LoadingContainer = styled.div`
  display: block;
  margin: auto;
  text-align: center;
`;

const OverlayMessageOffset = styled.div`
  position: relative;
  top: 30px;
  margin-left: ${Spacing[4]};
  margin-right: ${Spacing[4]};
`;

const OverlayMessageBox = styled.div`
  display: flex;
  width: fit-content;
  margin: auto;

  background: ${Color.PrimaryWhite};
  border-radius: ${Spacing[2]};
  border: 1px solid ${Color.CoreSlate100};
  padding: ${Spacing[3]};
  padding-left: ${Spacing[4]};
  padding-right: ${Spacing[4]};
`;

const OverlayMessageIconContainer = styled.div`
  margin: auto;
  margin-right: ${Spacing[2]};
  padding-top: 2px;

  > svg {
    height: 20px;
    width: 20px;
  }
`;

const CardContainer = styled(Card)<{ enableScroll: boolean }>`
  margin-bottom: 0px;
  ${({ enableScroll }) =>
    enableScroll &&
    css`
      overflow: auto;
    `}
`;

const ButtonWrapper = styled(HStack)`
  position: absolute;
  bottom: ${Spacing[3]};
  right: ${Spacing[3]};
  z-index: ${Z_INDICES.withShadow};

  height: unset;
  grid-auto-flow: row;
`;

const StyledButton = styled(Button)`
  border: 1px solid ${Color.CoreSlate100};
`;

const CardHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: ${Spacing[1]};
`;

const CardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  margin-right: auto;
`;

const CardHeaderRight = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const HeadingContainer = styled.div`
  white-space: nowrap;
  margin-top: auto;
`;

const StyledTooltip = styled(TooltipComponent)`
  z-index: ${Z_INDICES.floatingBar};
  position: relative;
`;

const StyledIconButton = styled(Button)`
  margin-right: ${Spacing[2]};
`;

interface GraphV2CardProps {
  accountTypes: AccountType[];
  nodeFunctionsConfigs: ServerNodeFunctionsConfig[];
  canvasButtons: ReactNode;
  isLoading: boolean;
  navigationHeader: ReactNode;
  overlayMessage: string | null;
  graphQuerySelector?: ReactNode;
  toggleExcludedNodesVisibility?: () => void;
  handleUpdateExcludedNodes?: (
    excludedNodes: NodeId[],
    hiddenNodes: NodeId[],
    visibleNodes: NodeId[],
  ) => void;
  enableScroll: boolean;
}

const GraphV2Card = ({
  accountTypes,
  nodeFunctionsConfigs,
  canvasButtons,
  handleUpdateExcludedNodes,
  isLoading,
  navigationHeader,
  graphQuerySelector,
  overlayMessage,
  enableScroll,
}: GraphV2CardProps) => {
  const useExportV2 = useFeatureFlag('dashboard:graph-use-export-v2');
  const nodeExclusionsEnabled = useFeatureFlag('dashboard:graph-excluded-nodes');
  const cardRef = useRef<HTMLElement>(null);
  const g6Wrapper = useG6Wrapper();
  const eventBus = useEventBus();
  const { exportNodes, graphExplorer, selectedNodes, excludedNodes, hiddenNodes, visibleNodes } =
    useGraphExplorer();

  // -----------------------------------------------------------------------------------------
  // Set up and mount G6.
  // -----------------------------------------------------------------------------------------

  const handleG6Mount = () => {
    eventBus.initialize(g6Wrapper, window, graphExplorer, true);
  };

  // -----------------------------------------------------------------------------------------
  // Canvas updates
  // -----------------------------------------------------------------------------------------
  const canvasUpdateCallback = useCallback(
    (message: G6GraphUpdateMessage) => {
      g6Wrapper.updateGraph(message);
    },
    [g6Wrapper],
  );
  useEffect(() => {
    graphExplorer.setCanvasUpdateCallback(canvasUpdateCallback);
  }, [graphExplorer, canvasUpdateCallback]);

  // -----------------------------------------------------------------------------------------
  // State mirrors from the GraphExplorer
  // -----------------------------------------------------------------------------------------

  const hoverCardXTransformer = (x: number) => {
    const currentHoveredNodeId = graphExplorer.getHoveredNodeId();
    const highlightedNode = currentHoveredNodeId
      ? graphExplorer.getNodeById(nodeIdToHashable(currentHoveredNodeId))
      : null;
    // For card, position hover card closer to the node than usual
    x -= 10;

    if (highlightedNode && highlightedNode.state.featured) {
      x += 12;
    }
    return x;
  };
  const hoverCardYTransformer = (y: number) => {
    // For card, position hover card closer to the node than usual
    y -= 35;
    return y;
  };

  // -----------------------------------------------------------------------------------------
  // Exclude Node Binding
  // -----------------------------------------------------------------------------------------
  const previousExcludedNodes = useRef<NodeId[]>(null);
  const previousHiddenNodes = useRef<NodeId[]>(null);
  const previousVisibleNodes = useRef<NodeId[]>(null);
  useEffect(() => {
    if (handleUpdateExcludedNodes) {
      if (!previousExcludedNodes.current) {
        // Initial render, don't do anything
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        previousExcludedNodes.current = [];
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        previousHiddenNodes.current = [];
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        previousVisibleNodes.current = [];
      } else if (
        previousExcludedNodes.current.length !== excludedNodes.length ||
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        previousHiddenNodes.current.length !== hiddenNodes.length ||
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        previousVisibleNodes.current.length !== visibleNodes.length
      ) {
        handleUpdateExcludedNodes(excludedNodes, hiddenNodes, visibleNodes);

        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        previousExcludedNodes.current = excludedNodes;
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        previousHiddenNodes.current = hiddenNodes;
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        previousVisibleNodes.current = visibleNodes;
      }
    }
  }, [JSON.stringify(excludedNodes), JSON.stringify(hiddenNodes), JSON.stringify(visibleNodes)]);

  // -----------------------------------------------------------------------------------------
  // Zoom logic
  // -----------------------------------------------------------------------------------------

  const handleZoomInClick = () => {
    graphExplorer.zoomIn();
    handleZoomPercentageChange(graphExplorer.getZoomPercentage());
  };

  const handleZoomOutClick = () => {
    graphExplorer.zoomOut();
    handleZoomPercentageChange(graphExplorer.getZoomPercentage());
  };

  const handleZoomPercentageChange = useCallback(
    (zoomPercentage: number) => {
      if (zoomPercentage !== graphExplorer.getZoomPercentage()) {
        graphExplorer.setZoomPercentage(zoomPercentage);
      }

      if (g6Wrapper.zoomPercentage() !== zoomPercentage) {
        g6Wrapper?.zoomSetPercentage(zoomPercentage);
      }
    },
    [g6Wrapper, graphExplorer],
  );

  useEffect(() => {
    g6Wrapper.setHandleZoomPercentageChange(handleZoomPercentageChange);
  }, [g6Wrapper, handleZoomPercentageChange]);

  // -----------------------------------------------------------------------------------------
  // Resize logic
  // -----------------------------------------------------------------------------------------
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 800, height: 500 });
  useEffect(() => {
    const handleResize = () => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const newWidth = cardRef.current?.offsetWidth - BASE * 6 || canvasDimensions.width;
      setCanvasDimensions({ width: newWidth, height: canvasDimensions.height });
    };

    // update canvas size upon window resize and once the card container is set
    if (cardRef.current) {
      handleResize();
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [canvasDimensions.height, canvasDimensions.width]);

  // -----------------------------------------------------------------------------------------
  // Graph export
  // -----------------------------------------------------------------------------------------
  const [exportModal, { enable: showExportModal, disable: hideExportModal }] = useToggle(false);

  // -----------------------------------------------------------------------------------------
  // Loading logic
  // -----------------------------------------------------------------------------------------
  const { areGraphConfigsFetched } = useGraphConfiguration();
  const canvasDisabled = isLoading || overlayMessage != null || !areGraphConfigsFetched;
  eventBus.setIsDisabled(canvasDisabled);

  const canvasWidthMinusCardBorder = canvasDimensions.width - 2 * BORDER_WIDTH;
  return (
    <CardContainer enableScroll={enableScroll}>
      <Card.Body ref={cardRef}>
        <CardHeader>
          <CardHeaderLeft>
            <HeadingContainer>
              <Heading size='sm'>Graph Results</Heading>
            </HeadingContainer>
            {graphQuerySelector}
          </CardHeaderLeft>
          <CardHeaderRight>{navigationHeader}</CardHeaderRight>
        </CardHeader>
        <GraphContainer>
          <CanvasContainer height={canvasDimensions.height} width={canvasDimensions.width}>
            <GraphCanvasV2
              handleG6Mount={handleG6Mount}
              height={canvasDimensions.height}
              selectedNodes={selectedNodes}
              width={canvasWidthMinusCardBorder}
            />
            <GraphHoverCard
              canvasHeight={canvasDimensions.height}
              canvasWidth={canvasWidthMinusCardBorder}
              getNodeRenderConfig={graphExplorer.getNodeRenderConfig}
              actions={(node: Node) => {
                const actions = [];
                if (node.id.token === ACCOUNT_DATUM_TYPE.token) {
                  actions.push(
                    <StyledTooltip
                      key='open'
                      placement='bottom'
                      trigger='hover'
                      tooltip='Open'
                      hideArrow={true}
                    >
                      <StyledIconButton
                        width='icon'
                        variant='basic'
                        type='button'
                        id='graph-account-hover-card__open-link-button'
                        onClick={() => {
                          window.open(`/dashboard/accounts/${node.id.value}`, '_blank');
                        }}
                        data-test='graph-account-hover-card__open-link-button'
                      >
                        <LinkExternalIcon />
                      </StyledIconButton>
                    </StyledTooltip>,
                  );
                }

                if (nodeExclusionsEnabled) {
                  if (
                    !excludedNodes.some(
                      (excludedNode: NodeId) =>
                        excludedNode.token === node.id.token &&
                        excludedNode.value === node.id.value,
                    )
                  ) {
                    actions.push(
                      <StyledTooltip
                        key='open'
                        placement='bottom'
                        trigger='hover'
                        tooltip='Exclude'
                        hideArrow={true}
                      >
                        <StyledIconButton
                          width='icon'
                          variant='basic'
                          type='button'
                          id='graph-account-hover-card__add-excluded-node-button'
                          onClick={() => {
                            graphExplorer.addExcludedNodes([node.id]);
                            g6Wrapper.updateGraph(graphExplorer.computeExclusions());
                          }}
                          data-test='graph-account-hover-card__remove-excluded-node-button'
                        >
                          <CircleMinusIcon />
                        </StyledIconButton>
                      </StyledTooltip>,
                    );
                  } else {
                    actions.push(
                      <StyledTooltip
                        key='open'
                        placement='bottom'
                        trigger='hover'
                        tooltip='Unexclude'
                        hideArrow={true}
                      >
                        <StyledIconButton
                          width='icon'
                          variant='basic'
                          type='button'
                          id='graph-account-hover-card__remove-excluded-node-button'
                          onClick={() => {
                            graphExplorer.removeExcludedNodes([node.id]);
                            g6Wrapper.updateGraph(graphExplorer.computeExclusions());
                          }}
                          data-test='graph-account-hover-card__remove-excluded-node-button'
                        >
                          <CirclePlusIcon />
                        </StyledIconButton>
                      </StyledTooltip>,
                    );
                  }
                }

                return actions;
              }}
              xTransformer={hoverCardXTransformer}
              yTransformer={hoverCardYTransformer}
            />
            {
              <ButtonWrapper>
                {/* Export button would be here */}
                <HStack spacing={Spacing[1]}>
                  {canvasButtons}
                  <Tooltip
                    iconTrigger={
                      <StyledButton
                        type='button'
                        width='icon'
                        size='sm'
                        variant='inverse'
                        onClick={showExportModal}
                      >
                        <IconDownload />
                      </StyledButton>
                    }
                    portal={true}
                    side='top'
                  >
                    <Paragraph color='primary'>Export Accounts</Paragraph>
                  </Tooltip>
                </HStack>
                <HStack spacing={Spacing[1]}>
                  <StyledButton
                    type='button'
                    width='icon'
                    size='sm'
                    variant='inverse'
                    onClick={handleZoomInClick}
                    disabled={!graphExplorer.canZoomIn()}
                  >
                    <PlusIcon />
                  </StyledButton>
                  <StyledButton
                    type='button'
                    width='icon'
                    size='sm'
                    variant='inverse'
                    onClick={handleZoomOutClick}
                    disabled={!graphExplorer.canZoomOut()}
                  >
                    <MinusIcon />
                  </StyledButton>
                </HStack>
              </ButtonWrapper>
            }
          </CanvasContainer>
          {canvasDisabled && (
            <DisabledOverlay>
              {overlayMessage ? (
                <OverlayMessageOffset>
                  <OverlayMessageBox>
                    <OverlayMessageIconContainer>
                      <CircleInfoIcon />
                    </OverlayMessageIconContainer>
                    {overlayMessage}
                  </OverlayMessageBox>
                </OverlayMessageOffset>
              ) : (
                <LoadingContainer>
                  <LoadingIndicator />
                  Loading Graph...
                </LoadingContainer>
              )}
            </DisabledOverlay>
          )}
          {useExportV2 ? (
            <GraphExportModalV2
              exportNodes={exportNodes}
              showGraphExportModal={exportModal}
              hideGraphExportModal={hideExportModal}
              accountTypes={accountTypes}
              nodeFunctionsConfigs={nodeFunctionsConfigs}
            />
          ) : (
            <GraphExportModal
              exportNodes={exportNodes}
              showGraphExportModal={exportModal}
              hideGraphExportModal={hideExportModal}
            />
          )}
        </GraphContainer>
      </Card.Body>
    </CardContainer>
  );
};

export default GraphV2Card;
